import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage, useAuth, api, JOB_CATEGORIES, COUNTRIES } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { 
  Bell, BellOff, Check, CheckCircle, Briefcase, Settings, 
  CreditCard, Loader2, ChevronRight, Gift, Zap, Crown
} from 'lucide-react';
import { toast } from 'sonner';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

// Worker Nav (simplified, import from WorkerDashboard in real app)
const WorkerNav = () => {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold text-brand">KEIKKA</Link>
          
          <div className="hidden md:flex items-center gap-6">
            <Link to="/worker/dashboard" className="text-muted-foreground hover:text-foreground transition">
              {t('nav.dashboard')}
            </Link>
            <Link to="/worker/notifications" className="text-muted-foreground hover:text-foreground transition flex items-center gap-1">
              <Bell className="h-4 w-4" />
              {t('nav.notifications')}
            </Link>
            <Link to="/jobs" className="text-muted-foreground hover:text-foreground transition">
              {t('landing.browseJobs')}
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <span className="text-sm text-muted-foreground">{user?.nickname}</span>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }}>
              {t('auth.logout')}
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Notifications Page
export const NotificationsPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [status, setStatus] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    if (user?.role !== 'worker') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [notifRes, statusRes, prefsRes] = await Promise.all([
        api.getNotifications({}),
        api.getNotificationStatus(),
        api.getNotificationPreferences()
      ]);
      setNotifications(notifRes.data.notifications || []);
      setStatus(statusRes.data);
      setPreferences(prefsRes.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await api.markNotificationRead(id);
      setNotifications(notifications.map(n => n.id === id ? {...n, read: true} : n));
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.markAllNotificationsRead();
      setNotifications(notifications.map(n => ({...n, read: true})));
      toast.success(t('common.success'));
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleSubscribe = async (planType) => {
    setSubscribing(true);
    try {
      const res = await api.createSubscription(planType);
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error(t('common.error'));
      setSubscribing(false);
    }
  };

  const handlePreferenceChange = async (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);
    try {
      await api.updateNotificationPreferences(newPrefs);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const toggleCategory = async (category) => {
    const categories = preferences.categories || [];
    const newCategories = categories.includes(category)
      ? categories.filter(c => c !== category)
      : [...categories, category];
    handlePreferenceChange('categories', newCategories);
  };

  const toggleCountry = async (code) => {
    const countries = preferences.countries || [];
    const newCountries = countries.includes(code)
      ? countries.filter(c => c !== code)
      : [...countries, code];
    handlePreferenceChange('countries', newCountries);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <WorkerNav />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Skeleton className="h-12 w-1/3 mb-8" />
          <Skeleton className="h-48 mb-4" />
          <Skeleton className="h-32" />
        </div>
      </div>
    );
  }

  const freeRemaining = status?.free_remaining || 0;
  const freeLimit = status?.free_limit || 5;
  const notificationsUsed = status?.notifications_used || 0;
  const isSubscribed = status?.subscription_status === 'active';
  const canReceive = status?.can_receive_notifications;

  return (
    <div className="min-h-screen bg-background">
      <WorkerNav />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="notifications-page">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">{t('notifications.title')}</h1>
            <p className="text-muted-foreground">{t('notifications.subtitle')}</p>
          </div>
          {notifications.some(n => !n.read) && (
            <Button variant="outline" onClick={handleMarkAllRead} data-testid="mark-all-read-btn">
              <Check className="mr-2 h-4 w-4" />
              {t('notifications.markAllRead')}
            </Button>
          )}
        </div>
        
        {/* Motivational banner */}
        <div className="bg-brand/5 border border-brand/20 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Zap className="h-5 w-5 text-brand mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium">{t('microcopy.notifications.aiFindsJobs')}</p>
              <p className="text-xs text-muted-foreground">{t('microcopy.notifications.noSpamEver')}</p>
              <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Check className="h-3 w-3 text-green-500" />
                  {t('microcopy.notifications.onlyRelevant')}
                </span>
                <span className="flex items-center gap-1">
                  <Check className="h-3 w-3 text-green-500" />
                  {t('microcopy.notifications.youControl')}
                </span>
                <span className="flex items-center gap-1">
                  <Check className="h-3 w-3 text-green-500" />
                  {t('microcopy.notifications.unsubscribeAnytime')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Notification Status Card */}
        <Card className="mb-8" data-testid="notification-status-card">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {isSubscribed ? (
                    <>
                      <Crown className="h-5 w-5 text-brand" />
                      {t('notifications.activeSubscription')}
                    </>
                  ) : (
                    <>
                      <Gift className="h-5 w-5 text-brand" />
                      {t('notifications.freeNotifications')}
                    </>
                  )}
                </CardTitle>
                <CardDescription>
                  {isSubscribed 
                    ? t('notifications.unlimitedAlerts')
                    : t('notifications.freeRemaining', { remaining: freeRemaining, total: freeLimit })
                  }
                </CardDescription>
              </div>
              {canReceive ? (
                <Badge className="bg-green-500">
                  <Bell className="h-3 w-3 mr-1" />
                  {t('notifications.active')}
                </Badge>
              ) : (
                <Badge variant="secondary">
                  <BellOff className="h-3 w-3 mr-1" />
                  {t('notifications.paused')}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!isSubscribed && (
              <>
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span>{t('notifications.freeNotifications')}</span>
                    <span>{notificationsUsed} / {freeLimit}</span>
                  </div>
                  <Progress value={(notificationsUsed / freeLimit) * 100} className="h-2" />
                </div>
                
                {!canReceive && (
                  <div className="bg-muted p-4 rounded-lg mb-4">
                    <p className="text-sm font-medium mb-2">
                      {t('notifications.usedAllFree')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t('notifications.subscribeToContine')}
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Subscription Options */}
            {!isSubscribed && (
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <Card className="border-2 hover:border-brand transition" data-testid="monthly-plan-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{t('notifications.monthly')}</CardTitle>
                    <div className="text-2xl font-bold">€2.99<span className="text-sm font-normal text-muted-foreground">{t('notifications.perMonth')}</span></div>
                  </CardHeader>
                  <CardContent className="pb-4">
                    <ul className="text-sm space-y-1 text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        {t('notifications.unlimitedJobAlerts')}
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        {t('notifications.cancelAnytime')}
                      </li>
                    </ul>
                  </CardContent>
                  <CardFooter>
                    <Button 
                      className="w-full" 
                      onClick={() => handleSubscribe('worker_notifications_monthly')}
                      disabled={subscribing}
                      data-testid="subscribe-monthly-btn"
                    >
                      {subscribing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {t('notifications.subscribeMonthly')}
                    </Button>
                  </CardFooter>
                </Card>

                <Card className="border-2 border-brand relative" data-testid="yearly-plan-card">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-brand">{t('notifications.save30')}</Badge>
                  </div>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{t('notifications.yearly')}</CardTitle>
                    <div className="text-2xl font-bold">€24.99<span className="text-sm font-normal text-muted-foreground">{t('notifications.perYear')}</span></div>
                  </CardHeader>
                  <CardContent className="pb-4">
                    <ul className="text-sm space-y-1 text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        {t('notifications.unlimitedJobAlerts')}
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        {t('notifications.bestValue')}
                      </li>
                    </ul>
                  </CardContent>
                  <CardFooter>
                    <Button 
                      className="w-full bg-brand hover:bg-brand/90" 
                      onClick={() => handleSubscribe('worker_notifications_yearly')}
                      disabled={subscribing}
                      data-testid="subscribe-yearly-btn"
                    >
                      {subscribing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {t('notifications.subscribeYearly')}
                    </Button>
                  </CardFooter>
                </Card>
              </div>
            )}

            {isSubscribed && (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                <span>{t('notifications.unlimitedAlerts')}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notification Preferences */}
        <Card className="mb-8" data-testid="notification-preferences-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              {t('notifications.preferences')}
            </CardTitle>
            <CardDescription>
              {t('notifications.preferencesSubtitle')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="job_alerts">{t('notifications.jobAlerts')}</Label>
                <p className="text-sm text-muted-foreground">{t('notifications.jobAlertsDesc')}</p>
              </div>
              <Switch
                id="job_alerts"
                checked={preferences?.job_alerts !== false}
                onCheckedChange={(checked) => handlePreferenceChange('job_alerts', checked)}
                data-testid="job-alerts-switch"
              />
            </div>

            <Separator />

            <div>
              <Label className="mb-3 block">{t('notifications.categoriesFilter')}</Label>
              <div className="flex flex-wrap gap-2">
                {JOB_CATEGORIES.map((cat) => (
                  <Badge
                    key={cat}
                    variant={(preferences?.categories || []).includes(cat) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => toggleCategory(cat)}
                    data-testid={`pref-category-${cat}`}
                  >
                    {t(`categories.${cat}`)}
                  </Badge>
                ))}
              </div>
            </div>

            <Separator />

            <div>
              <Label className="mb-3 block">{t('notifications.countriesFilter')}</Label>
              <div className="flex flex-wrap gap-2">
                {COUNTRIES.map((country) => (
                  <Badge
                    key={country.code}
                    variant={(preferences?.countries || []).includes(country.code) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => toggleCountry(country.code)}
                    data-testid={`pref-country-${country.code}`}
                  >
                    {t(country.nameKey)}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Notifications */}
        <Card data-testid="notifications-list-card">
          <CardHeader>
            <CardTitle>{t('notifications.recentNotifications')}</CardTitle>
          </CardHeader>
          <CardContent>
            {notifications.length === 0 ? (
              <div className="text-center py-8">
                <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{t('notifications.noNotifications')}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('notifications.noNotificationsDesc')}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 rounded-lg border transition ${
                      notification.read ? 'bg-background' : 'bg-muted/50 border-brand/20'
                    }`}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-full ${notification.read ? 'bg-muted' : 'bg-brand/10'}`}>
                          <Briefcase className={`h-4 w-4 ${notification.read ? 'text-muted-foreground' : 'text-brand'}`} />
                        </div>
                        <div>
                          <p className={`font-medium ${notification.read ? '' : 'text-brand'}`}>
                            {notification.title}
                          </p>
                          <p className="text-sm text-muted-foreground">{notification.message}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(notification.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {notification.job_id && (
                          <Link to={`/jobs/${notification.job_id}`}>
                            <Button size="sm" variant="outline" data-testid={`view-job-${notification.id}`}>
                              {t('notifications.viewJob')}
                            </Button>
                          </Link>
                        )}
                        {!notification.read && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleMarkRead(notification.id)}
                            data-testid={`mark-read-${notification.id}`}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Box */}
        <Card className="mt-8 bg-muted/50">
          <CardContent className="py-4">
            <div className="flex items-start gap-4">
              <Zap className="h-6 w-6 text-brand flex-shrink-0 mt-1" />
              <div>
                <p className="font-medium">{t('notifications.howItWorks')}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('notifications.howItWorksDesc', { limit: freeLimit })}
                  <strong className="text-foreground"> {t('notifications.browsingFree')}</strong>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NotificationsPage;
